import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, FileCheck2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, PriorityBadge, StatusBadge, EmptyState, Card } from "@/components/common";
import { relativeTime } from "@/lib/format";

export default function Approvals() {
  const { companyParam, companyId } = useApp();
  const navigate = useNavigate();
  const [filter, setFilter] = React.useState("pending");

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ["approvals", companyId, filter],
    queryFn: async () => (await api.get("/approvals", { params: { ...companyParam, ...(filter !== "all" ? { status: filter } : {}) } })).data,
  });

  const tabs = [["pending", "Pending"], ["approved", "Approved"], ["all", "All"]];

  return (
    <div data-testid="approvals-page">
      <PageHeader
        title="Approval Center"
        subtitle="Nothing reaches a client without your sign-off. Each package includes the summary, sources used, key decisions, risks and validation checks."
      />

      <div className="flex items-center gap-1 mb-4 border-b border-border">
        {tabs.map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            data-testid={`approval-tab-${val}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === val ? "border-brand text-brand" : "border-transparent text-ink-muted hover:text-ink"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-ink-muted">Loading…</div>
      ) : approvals.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No approvals here" hint="Client-facing deliverables will appear here for your review." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {approvals.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/approvals/${a.id}`)}
              data-testid={`approval-card-${a.id}`}
              className="text-left bg-white border border-border hover:border-ink hover:-translate-y-[1px] hover:shadow-sm transition-all p-4 group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <PriorityBadge value={a.priority} />
                <StatusBadge value={a.status} />
              </div>
              <h3 className="font-heading text-base font-semibold text-ink leading-tight">{a.deliverable}</h3>
              <p className="text-sm text-ink-muted mt-0.5">{a.client_name}</p>
              <p className="text-sm text-ink-muted mt-2 line-clamp-2">{a.summary}</p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-3 text-[11px] font-mono text-ink-faint">
                  <span className="flex items-center gap-1"><FileCheck2 className="w-3 h-3" /> {a.sources?.length || 0} sources</span>
                  <span className={`flex items-center gap-1 ${a.validation_passed ? "text-st-success" : "text-st-warning"}`}>
                    {a.validation_passed ? <FileCheck2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                    {a.validation_passed ? "validated" : "warnings"}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-xs font-semibold text-brand">Review <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
